import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Boxes, CheckCircle2, CircleAlert, ListChecks, ScanLine } from 'lucide-react';
import { formatDate, formatQty, type Paginated } from '@erp/shared';
import { api } from '@/lib/api';
import { useDebounced } from '@/hooks/useDebounced';
import { PageHeader, SearchInput, ErrorState, EmptyState, StatCard, ProgressBar } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { toast } from 'sonner';

interface KitRow {
  id: string; number: string; status: string; required_count: number; scanned_count: number;
  created_at: string; completed_at: string | null; qr_code: string; unit_status: string;
  width_mm: number; height_mm: number; model_name: string; order_number: string | null;
  promised_date: string | null; customer_name: string | null;
  total_lines: number; pending_lines: number;
}

interface KitStats { pending: number; in_progress: number; blocked: number; completed_today: number }

const FILTERS: [string, string][] = [
  ['open', 'A preparar'],
  ['pending', 'Pendientes'],
  ['in_progress', 'En preparación'],
  ['blocked', 'Bloqueados'],
  ['completed', 'Completados'],
  ['', 'Todos']
];

function deliveryHint(value: string | null) {
  if (!value) return { label: 'Sin fecha', className: 'text-muted-foreground' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(value);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { label: `${formatDate(value)} · vencido`, className: 'text-red-600 font-medium' };
  if (days <= 3) return { label: `${formatDate(value)} · ${days === 0 ? 'hoy' : `en ${days} d`}`, className: 'text-amber-600 font-medium' };
  return { label: formatDate(value), className: 'text-muted-foreground' };
}

export function KitsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  // `?status=…` viene de los paneles del cuadro de mando: solo si es un filtro conocido.
  const [searchParams, setSearchParams] = useSearchParams();
  const estadoInicial = searchParams.get('status') ?? 'open';
  const [status, setStatus] = useState(FILTERS.some(([value]) => value === estadoInicial) ? estadoInicial : 'open');
  useEffect(() => {
    const enUrl = searchParams.get('status') ?? 'open';
    setStatus(FILTERS.some(([value]) => value === enUrl) ? enUrl : 'open');
    setPage(1);
  }, [searchParams]);
  const cambiarEstado = (valor: string) => {
    setStatus(valor);
    setPage(1);
    setSearchParams((params) => {
      params.set('status', valor);
      return params;
    }, { replace: true });
  };
  const [page, setPage] = useState(1);
  const [scan, setScan] = useState('');

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (debounced) params.set('q', debounced);
  if (status) params.set('status', status);

  const kitsQuery = useQuery({
    queryKey: ['kits', page, debounced, status],
    queryFn: () => api<Paginated<KitRow>>(`/kits?${params.toString()}`)
  });
  const statsQuery = useQuery({ queryKey: ['kit-stats'], queryFn: () => api<KitStats>('/kits/stats') });

  const openByQr = async () => {
    if (!scan.trim()) return;
    try {
      const kit = await api<{ id: string }>(`/kits/by-qr/${encodeURIComponent(scan.trim())}`);
      navigate(`/kits/${kit.id}`);
      setScan('');
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const columns: Column<KitRow>[] = [
    {
      key: 'kit',
      header: 'Kit / QR',
      cell: (row) => (
        <div>
          <p className="font-mono text-xs font-semibold">{row.number}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{row.qr_code}</p>
        </div>
      )
    },
    {
      key: 'product',
      header: 'Modelo',
      cell: (row) => (
        <div>
          <p className="font-medium">{row.model_name}</p>
          <p className="text-[10px] text-muted-foreground">{row.width_mm}×{row.height_mm} mm · {row.order_number ?? 'Sin pedido'}</p>
        </div>
      )
    },
    {
      key: 'customer',
      header: 'Cliente / entrega',
      cell: (row) => {
        const hint = deliveryHint(row.promised_date);
        return (
          <div>
            <p className="text-muted-foreground">{row.customer_name ?? '—'}</p>
            <p className={`text-[10px] ${hint.className}`}>{hint.label}</p>
          </div>
        );
      }
    },
    { key: 'status', header: 'Estado', cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'progress',
      header: 'Progreso',
      cell: (row) => {
        const total = row.total_lines || row.required_count || 1;
        const pending = row.pending_lines ?? 0;
        const done = Math.max(0, total - pending);
        const complete = pending === 0;
        return (
          <div className="w-40">
            <div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
              <span>{formatQty(done, 0)} / {formatQty(total, 0)} líneas</span>
              <span>{Math.round((done / total) * 100)}%</span>
            </div>
            <ProgressBar value={(done / total) * 100} tone={complete ? 'success' : 'primary'} />
          </div>
        );
      }
    },
    {
      key: 'missing',
      header: 'Faltan',
      cell: (row) => row.pending_lines > 0
        ? <Badge tone="warning">{formatQty(row.pending_lines, 0)} líneas</Badge>
        : <Badge tone="success"><CheckCircle2 /> Completo</Badge>
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <Button
          variant={row.status === 'pending' || row.status === 'in_progress' ? 'default' : 'outline'}
          size="sm"
          onClick={(event) => { event.stopPropagation(); navigate(`/kits/${row.id}`); }}
        >
          {row.status === 'completed' ? 'Abrir' : 'Preparar'}
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Preparación de kits"
        description="Cola del puesto de almacén, ordenada por fecha de entrega. Abre un kit o escanea su etiqueta de seguimiento."
        actions={(
          <form onSubmit={(event) => { event.preventDefault(); void openByQr(); }} className="flex items-center gap-2">
            <div className="relative">
              <ScanLine className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                value={scan}
                onChange={(event) => setScan(event.target.value)}
                placeholder="Escanear QR de unidad o kit…"
                className="field h-9 w-64 pl-8 font-mono text-xs"
                autoComplete="off"
              />
            </div>
            <Button type="submit" variant="outline" disabled={!scan.trim()}>Abrir kit</Button>
          </form>
        )}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Pendientes" value={statsQuery.data?.pending ?? '—'} icon={Boxes} tone="warning" hint="Sin empezar" />
        <StatCard label="En preparación" value={statsQuery.data?.in_progress ?? '—'} icon={ListChecks} tone="primary" hint="Escaneando" />
        <StatCard label="Bloqueados" value={statsQuery.data?.blocked ?? '—'} icon={CircleAlert} tone="danger" hint="Requieren revisión" />
        <StatCard label="Completados hoy" value={statsQuery.data?.completed_today ?? '—'} icon={CheckCircle2} tone="success" hint="Stock consumido" />
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar kit, QR, modelo, pedido o cliente…" className="w-full sm:w-80" />
          {FILTERS.map(([value, label]) => (
            <Button key={value || 'all'} size="sm" variant={status === value ? 'default' : 'outline'} onClick={() => cambiarEstado(value)}>
              {label}
            </Button>
          ))}
        </div>
        {status === 'open' ? (
          <div className="flex items-center gap-2 border-b border-border bg-blue-50/60 px-3 py-2 text-[11px] text-blue-800 dark:bg-blue-500/10 dark:text-blue-300">
            <ScanLine className="size-3.5" />
            Cola de preparación: {kitsQuery.data?.meta.total ?? 0} kits. La prioridad la marca la fecha de entrega del pedido.
          </div>
        ) : null}
        {kitsQuery.error ? <ErrorState message={(kitsQuery.error as Error).message} onRetry={() => void kitsQuery.refetch()} /> : (
          <DataTable
            columns={columns} rows={kitsQuery.data?.data ?? []} loading={kitsQuery.isLoading}
            rowKey={(row) => row.id} onRowClick={(row) => navigate(`/kits/${row.id}`)}
            empty={<EmptyState title={status === 'open' ? 'No hay kits a preparar' : 'Sin kits'} description="Los kits se generan al liberar unidades de un pedido confirmado." icon={Boxes} />}
            footer={<Pagination page={page} totalPages={kitsQuery.data?.meta.totalPages ?? 1} total={kitsQuery.data?.meta.total ?? 0} onPageChange={setPage} />}
          />
        )}
      </Card>
    </div>
  );
}
